import React, { useState, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Alert, Button, Col, Row, Table } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import { useAuth } from '../../shared/AuthContext';
import { api } from '../../shared/api';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import { CenteredLoader } from '../../components/LoadingSpinner';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import StatsCard from '../../components/ui/StatsCard';
import StatusBadge from '../../components/ui/StatusBadge';
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '../../shared/DateFormatContext';
import './Dashboard.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Tooltip, Legend, Filler);

const DASHBOARD_DUMMY_DATA = {
  total_items: 123,
  total_projects: 24,
  total_contracts: 18,
  total_customers: 56,
  total_rentals: 45,
  total_stocks: 67,
  total_invoices: 89,
  total_paid: 56,
  total_unpaid: 33,
  total_after_due: 7,
  project_statuses: [{ status__name: 'Aktivní', status__color: '#206bc4', count: 12 }, { status__name: 'Dokončený', status__color: '#2fb344', count: 8 }],
  invoice_statuses: [{ status: 'paid', count: 56 }, { status: 'open', count: 26 }, { status: 'overdue', count: 7 }],
  stock_summary: { owned: 320, available: 245, allocated: 75 },
  user_activities: [
    {
      type: 'Project',
      app_label: 'projects',
      model: 'project',
      pk: 1,
      history: [
        {
          history_user_id: 1,
          history_user: { profile: { first_name: 'Admin', last_name: 'User', photo: null } },
          history_type: '+',
          instance: { pk: 1 },
          name: 'New Project XYZ',
          history_date: '2025-08-15 12:34:56'
        }
      ]
    },
    {
      type: 'Customer',
      app_label: 'customers',
      model: 'customer',
      pk: 2,
      history: [
        {
          history_user_id: 2,
          history_user: { profile: { first_name: 'John', last_name: 'Doe', photo: null } },
          history_type: '~',
          instance: { pk: 2 },
          name: 'Company ABC Ltd.',
          history_date: '2025-08-14 15:45:23'
        }
      ]
    }
  ],
  chart_labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'],
  chart_data: [12, 19, 8, 15, 22, 14, 18, 25],
  project_chart_data: [8, 12, 6, 11, 17, 10, 14, 20],
};

function Dashboard() {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(false);
  const { hasPermission } = useAuth();
  const _statsDebounce = useRef(null);
  const queryClient = useQueryClient();

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => {
      try {
        const d = await api.get('dashboard/api/dashboard/stats/');
        setError(null);
        setAuthError(false);
        return d;
      } catch (apiError) {
        if (apiError.message.includes('Authentication required') ||
            apiError.message.includes('Session expired') ||
            apiError.message.includes('log in')) {
          setAuthError(true);
          setError('Authentication required. Please log in to view dashboard statistics.');
        } else {
          setError(`Error connecting to API: ${apiError.message}`);
          setAuthError(false);
        }
        return DASHBOARD_DUMMY_DATA;
      }
    },
  });

  const stats = {
    total_items: dashboardData?.total_items || 0,
    total_projects: dashboardData?.total_projects || 0,
    total_contracts: dashboardData?.total_contracts || 0,
    total_customers: dashboardData?.total_customers || 0,
    total_rentals: dashboardData?.total_rentals || 0,
    total_stocks: dashboardData?.total_stocks || 0,
    total_invoices: dashboardData?.total_invoices || 0,
    total_paid: dashboardData?.total_paid || 0,
    total_unpaid: dashboardData?.total_unpaid || 0,
  };
  const userActivities = dashboardData?.user_activities || [];
  const chartData = useMemo(() => ({
    labels: dashboardData?.chart_labels || [],
    rentals: dashboardData?.chart_data || [],
    projects: dashboardData?.project_chart_data || [],
  }), [dashboardData]);
  const projectStatuses = dashboardData?.project_statuses || [];
  const invoiceStatuses = dashboardData?.invoice_statuses || [];
  const stockSummary = dashboardData?.stock_summary || { owned: 0, available: 0, allocated: 0 };
  const activityRows = useMemo(() => (
    userActivities.flatMap((activity, activityIndex) =>
      (activity.history || []).map((history, historyIndex) => ({
        key: `${activityIndex}-${historyIndex}`,
        activity,
        history,
      }))
    )
  ), [userActivities]);
  const metrics = useMemo(() => ([
    { key: 'stocks', title: t('dashboard.totalWarehouses'), value: stats.total_stocks, icon: 'building-warehouse', colorClass: 'primary' },
    { key: 'items', title: t('dashboard.totalItems'), value: stats.total_items, icon: 'packages', colorClass: 'azure' },
    { key: 'projects', title: t('dashboard.totalProjects'), value: stats.total_projects, icon: 'briefcase', colorClass: 'indigo' },
    { key: 'contracts', title: t('dashboard.totalContracts'), value: stats.total_contracts, icon: 'file-description', colorClass: 'cyan' },
    { key: 'customers', title: t('dashboard.totalCustomers'), value: stats.total_customers, icon: 'users', colorClass: 'orange' },
    { key: 'rentals', title: t('dashboard.createdRentals'), value: stats.total_rentals, icon: 'checklist', colorClass: 'green' },
    { key: 'invoices', title: t('dashboard.issuedInvoices'), value: stats.total_invoices, icon: 'file-invoice', colorClass: 'yellow' },
    { key: 'overdue', title: t('dashboard.overdueInvoices'), value: stats.total_after_due, icon: 'alert-triangle', colorClass: 'red' },
  ]), [stats, t]);
  const chartPeak = useMemo(() => {
    const values = [...chartData.rentals, ...chartData.projects].filter((value) => Number.isFinite(value));
    if (!values.length) {
      return 1;
    }

    return Math.max(...values);
  }, [chartData]);
  const rentalsTrendData = useMemo(() => ({
    labels: chartData.labels,
    datasets: [
      {
        label: t('dashboard.statistics.rentals'),
        data: chartData.rentals,
        fill: true,
        tension: 0.35,
        borderColor: 'rgba(54, 162, 235, 0.95)',
        backgroundColor: 'rgba(54, 162, 235, 0.18)',
        borderWidth: 3,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: 'rgba(54, 162, 235, 0.95)',
        pointBorderWidth: 2,
      },
      {
        label: t('dashboard.statistics.projects'),
        data: chartData.projects,
        fill: false,
        tension: 0.35,
        borderColor: 'rgba(112, 72, 232, 0.95)',
        borderWidth: 3,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: 'rgba(112, 72, 232, 0.95)',
        pointBorderWidth: 2,
      },
    ],
  }), [chartData, t]);
  const rentalsTrendOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 10,
        right: 8,
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      tooltip: {
        intersect: false,
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8,
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: chartPeak <= 3 ? chartPeak + 1 : Math.ceil(chartPeak * 1.2),
        grid: {
          color: 'rgba(98, 105, 118, 0.12)',
        },
        border: {
          display: false,
        },
        ticks: {
          precision: 0,
          stepSize: chartPeak <= 5 ? 1 : undefined,
        },
      },
    },
  }), [chartPeak]);
  const projectStatusData = useMemo(() => ({
    labels: projectStatuses.map((status) => status.status__name),
    datasets: [{ data: projectStatuses.map((status) => status.count), backgroundColor: projectStatuses.map((status) => status.status__color || '#6c7a91'), borderWidth: 0 }],
  }), [projectStatuses]);
  const invoiceStatusData = useMemo(() => {
    const labels = { open: t('dashboard.invoiceOpen'), paid: t('dashboard.invoicePaid'), overdue: t('dashboard.invoiceOverdue') };
    const colors = { open: '#f59f00', paid: '#2fb344', overdue: '#d63939' };
    return {
      labels: invoiceStatuses.map((status) => labels[status.status] || status.status),
      datasets: [{ data: invoiceStatuses.map((status) => status.count), backgroundColor: invoiceStatuses.map((status) => colors[status.status] || '#6c7a91'), borderWidth: 0 }],
    };
  }, [invoiceStatuses, t]);
  const doughnutOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, usePointStyle: true } } },
  }), []);

  useRealtimeEvents((e) => {
    if (e.type === 'model.changed') {
      clearTimeout(_statsDebounce.current);
      _statsDebounce.current = setTimeout(() =>
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'stats'] }), 500);
    }
  });

  // Render login suggestion if authentication error
  const renderAuthError = () => (
    <PageSection>
      <EmptyState
        icon={<i className="ti ti-lock app-empty-state__icon-glyph" aria-hidden="true" />}
        title={t('dashboard.error.authRequired')}
        message={t('dashboard.loginNeeded')}
        action={<Button as={Link} to="/accounts/login/">{t('dashboard.goToLogin')}</Button>}
      />
    </PageSection>
  );

  // Function to render history type as a coloured badge
  const renderHistoryType = (type) => {
    switch(type) {
      case '+':
        return <StatusBadge color="#2fb344" label={t('dashboard.added')} />;
      case '~':
        return <StatusBadge color="#4299e1" label={t('dashboard.modified')} />;
      case '-':
        return <StatusBadge color="#d63939" label={t('dashboard.removed')} />;
      default:
        return <StatusBadge color="#6c7a91" label={t('dashboard.otherAction')} />;
    }
  };

  // Map model name → frontend detail route. Returns null if no dedicated route exists.
  const getActivityLink = (activity) => {
    const pk = activity.pk;
    const extra = activity.extra || {};
    switch (activity.model) {
      case 'project':               return `/projects/detail/${pk}`;
      case 'projectoffer':          return extra.project_pk ? `/projects/detail/${extra.project_pk}/offers` : null;
      case 'contract':              return `/contracts/contract/${pk}`;
      case 'contractoffer':         return extra.contract_pk ? `/contracts/contract/${extra.contract_pk}` : null;
      case 'rental':                return `/rentals/rental/${pk}`;
      case 'customer':              return `/customers/customer/${pk}`;
      case 'stock':                 return `/warehouses/stock/${pk}`;
      case 'item':                  return extra.stock_pk ? `/warehouses/stock/${extra.stock_pk}` : '/warehouses/products';
      case 'product':               return `/warehouses/products/${pk}`;
      case 'productcategory':       return '/warehouses/products/categories';
      case 'producttype':           return '/warehouses/products/types';
      case 'vehicle':               return `/transport/${pk}`;
      case 'crewmember':            return `/crew/${pk}`;
      default:                      return null;
    }
  };

  // Map app_label+model to a human-readable coloured badge
  const MODEL_BADGE_COLORS = {
    project: 'bg-blue-lt',
    projectoffer: 'bg-indigo-lt',
    contract: 'bg-cyan-lt',
    contractoffer: 'bg-azure-lt',
    rental: 'bg-teal-lt',
    invoice: 'bg-yellow-lt',
    customer: 'bg-orange-lt',
    stock: 'bg-lime-lt',
    item: 'bg-green-lt',
    itemtype: 'bg-purple-lt',
    itemcategory: 'bg-pink-lt',
    product: 'bg-blue-lt',
    producttype: 'bg-indigo-lt',
    productcategory: 'bg-cyan-lt',
    vehicle: 'bg-red-lt',
    crewmember: 'bg-secondary-lt',
  };
  const renderTypeBadge = (model, label) => {
    const palette = {
      'bg-blue-lt': '#206bc4',
      'bg-indigo-lt': '#4263eb',
      'bg-cyan-lt': '#1098ad',
      'bg-azure-lt': '#4299e1',
      'bg-teal-lt': '#0ca678',
      'bg-yellow-lt': '#f59f00',
      'bg-orange-lt': '#f76707',
      'bg-lime-lt': '#74b816',
      'bg-green-lt': '#2f9e44',
      'bg-purple-lt': '#7048e8',
      'bg-pink-lt': '#d6336c',
      'bg-taupe-lt': '#8d6e63',
      'bg-muted-lt': '#6c757d',
      'bg-red-lt': '#e03131',
      'bg-secondary-lt': '#6c7a91',
    };
    const color = palette[MODEL_BADGE_COLORS[model] || 'bg-secondary-lt'];
    return <StatusBadge color={color} label={label} />;
  };

  // Function to render user initials for avatar
  const renderUserInitials = (user) => {
    if (!user || !user.profile) return '';
    return `${user.profile.first_name.slice(0, 1)}${user.profile.last_name.slice(0, 1)}`;
  };

  return (
    <PageShell
      pretitle={t('dashboard.overview')}
      title={t('dashboard.title')}
      description={t('dashboard.subtitle')}
      actions={hasPermission && hasPermission('auth.add_user') && hasPermission('accounts.add_profile') ? (
        <Button as={Link} to="/settings/groups">
          <i className="ti ti-user-plus me-2" />
          {t('dashboard.newUser')}
        </Button>
      ) : null}
    >
      {isLoading ? (
        <PageSection>
          <CenteredLoader message={t('dashboard.loadingData')} />
        </PageSection>
      ) : authError ? (
        renderAuthError()
      ) : error ? (
        <PageSection>
          <Alert variant="danger" className="app-inline-alert mb-0">{error}</Alert>
        </PageSection>
      ) : (
        <>
          <div className="app-stat-grid">
            {metrics.map((metric) => (
              <StatsCard
                key={metric.key}
                title={metric.title}
                value={metric.value}
                icon={metric.icon}
                colorClass={metric.colorClass}
              />
            ))}
          </div>

          <Row className="g-4">
            <Col lg={6}>
              <PageSection
                title={t('dashboard.recentActivity.title')}
                description={t('dashboard.recentActivity.subtitle')}
                className="dashboard-activity-card app-section-stretch"
                bodyClassName="p-0"
                actions={<Link to="/dashboard/activity" className="btn btn-sm btn-outline-secondary">{t('dashboard.recentActivity.viewAll')}</Link>}
              >
                {activityRows.length > 0 ? (
                  <div className="table-responsive h-100">
                    <Table hover className="table-vcenter app-table mb-0 align-middle dashboard-table">
                      <thead className="sticky-top">
                      <tr>
                        <th>{t('dashboard.recentActivity.user')}</th>
                        <th className="d-xs-none">{t('dashboard.recentActivity.action')}</th>
                        <th className="d-mobile-none">{t('dashboard.recentActivity.type')}</th>
                        <th>{t('dashboard.recentActivity.item')}</th>
                        <th className="d-mobile-none">{t('dashboard.recentActivity.dateAndTime')}</th>
                      </tr>
                      </thead>
                      <tbody>
                        {activityRows.map(({ key, activity, history }) => (
                          <tr key={key}>
                            <td className="w-1">
                              {history.history_user_id ? (
                                <Link to={`/accounts/profile/${history.history_user_id}`}>
                                  {history.history_user?.profile?.photo ? (
                                    <span className="avatar avatar-sm" style={{ backgroundImage: `url(${history.history_user.profile.photo.replace(/^https?:/, window.location.protocol)})` }}></span>
                                  ) : (
                                    <span className="avatar avatar-sm">{renderUserInitials(history.history_user)}</span>
                                  )}
                                </Link>
                              ) : (
                                <span className="avatar avatar-sm bg-secondary-lt text-secondary">{t('dashboard.recentActivity.anonymousInitial')}</span>
                              )}
                            </td>
                            <td className="d-xs-none">{renderHistoryType(history.history_type)}</td>
                            <td className="d-mobile-none">{renderTypeBadge(activity.model, activity.type)}</td>
                            <td className="td-truncate">
                              <div className="text-truncate fw-medium">
                                {(() => {
                                  const link = getActivityLink(activity);
                                  return link ? <Link to={link}>{history.name}</Link> : history.name;
                                })()}
                              </div>
                            </td>
                            <td className="text-nowrap text-secondary d-mobile-none">{formatDateTime(history.history_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <EmptyState
                    icon={<i className="ti ti-history app-empty-state__icon-glyph" aria-hidden="true" />}
                    title={t('dashboard.recentActivity.emptyTitle')}
                    message={t('dashboard.recentActivity.emptyMessage')}
                  />
                )}
              </PageSection>
            </Col>
            <Col lg={6}>
              <PageSection
                title={t('dashboard.statistics.title')}
                description={t('dashboard.statistics.subtitle')}
                className="dashboard-chart-card"
              >
                {chartData.labels.length > 0 ? (
                  <div className="dashboard-chart-container">
                    <Line data={rentalsTrendData} options={rentalsTrendOptions} />
                  </div>
                ) : (
                  <EmptyState
                    icon={<i className="ti ti-chart-bar app-empty-state__icon-glyph" aria-hidden="true" />}
                    title={t('dashboard.statistics.emptyTitle')}
                    message={t('dashboard.statistics.emptyMessage')}
                  />
                )}
              </PageSection>
            </Col>
          </Row>
          <Row className="g-4 mt-0">
            <Col lg={4}>
              <PageSection title={t('dashboard.projectStatus.title')} description={t('dashboard.projectStatus.subtitle')} className="dashboard-insight-card">
                {projectStatuses.length ? <div className="dashboard-doughnut-container"><Doughnut data={projectStatusData} options={doughnutOptions} /></div> : <EmptyState icon={<i className="ti ti-briefcase app-empty-state__icon-glyph" />} title={t('dashboard.projectStatus.emptyTitle')} />}
              </PageSection>
            </Col>
            <Col lg={4}>
              <PageSection title={t('dashboard.invoiceStatus.title')} description={t('dashboard.invoiceStatus.subtitle')} className="dashboard-insight-card">
                {invoiceStatuses.length ? <div className="dashboard-doughnut-container"><Doughnut data={invoiceStatusData} options={doughnutOptions} /></div> : <EmptyState icon={<i className="ti ti-file-invoice app-empty-state__icon-glyph" />} title={t('dashboard.invoiceStatus.emptyTitle')} />}
              </PageSection>
            </Col>
            <Col lg={4}>
              <PageSection title={t('dashboard.stockSummary.title')} description={t('dashboard.stockSummary.subtitle')} className="dashboard-insight-card">
                <div className="dashboard-stock-summary">
                  <div><span>{t('dashboard.stockSummary.owned')}</span><strong>{stockSummary.owned}</strong></div>
                  <div><span>{t('dashboard.stockSummary.available')}</span><strong className="text-success">{stockSummary.available}</strong></div>
                  <div><span>{t('dashboard.stockSummary.allocated')}</span><strong className="text-warning">{stockSummary.allocated}</strong></div>
                </div>
              </PageSection>
            </Col>
          </Row>
        </>
      )}
    </PageShell>
  );
}

export default Dashboard;
